/**
 * Email-link handling for invites and password resets.
 *
 * Supabase's invite and reset emails point at its own `/auth/v1/verify`
 * endpoint, which confirms the token server-side and then redirects to our
 * Site URL with the session in the URL *fragment*:
 *
 *   https://roof-board.com/#access_token=…&refresh_token=…&type=invite
 *
 * The client is deliberately created with `detectSessionInUrl: false`, so
 * nothing picks that up on its own — which is exactly why invited users used to
 * land on the login form with no session and no password. We read the fragment
 * ourselves instead, which also lets us tell an invite from an ordinary sign-in
 * and send the user to the set-password screen.
 *
 * The fragment is cleared as soon as it is read. Access tokens in a URL end up
 * in browser history and in the `Referer` of anything the page loads, and there
 * is no reason to leave them lying there once we hold the session.
 */

export type AuthLinkType = "invite" | "recovery" | "other";

export interface AuthLink {
  /** What kind of email link brought the user here, if any. */
  type: AuthLinkType | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** Supabase's message when the link was expired or already used. */
  errorDescription: string | null;
}

const EMPTY: AuthLink = {
  type: null,
  accessToken: null,
  refreshToken: null,
  errorDescription: null,
};

/** Pure parser, so the awkward cases can be tested without a browser. */
export function parseAuthLink(hash: string): AuthLink {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (raw === "") return EMPTY;

  const params = new URLSearchParams(raw);
  const type = params.get("type");
  const accessToken = params.get("access_token");
  const errorDescription = params.get("error_description");

  // A fragment that carries neither a session nor an error is somebody else's
  // (a plain `#section` anchor, say). Leave it alone.
  if (!accessToken && !errorDescription) return EMPTY;

  return {
    type:
      type === "invite" || type === "recovery" ? type : type ? "other" : null,
    accessToken,
    refreshToken: params.get("refresh_token"),
    errorDescription,
  };
}

/** True when this link should land the user on the set-password screen. */
export function requiresNewPassword(link: AuthLink): boolean {
  return (
    !link.errorDescription &&
    link.accessToken !== null &&
    (link.type === "invite" || link.type === "recovery")
  );
}

function consume(): AuthLink {
  if (typeof window === "undefined") return EMPTY;

  const link = parseAuthLink(window.location.hash);
  if (link === EMPTY) return EMPTY;

  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search,
  );
  return link;
}

/**
 * Read once, at module load.
 *
 * Deliberately not inside a hook: React's StrictMode mounts components twice in
 * development, and a second read would find the fragment already cleared.
 */
export const authLink: AuthLink = consume();
